import Cookies from 'js-cookie';
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  PhoenixLegalPrivacyConsentResponseResult,
  GetFilteredPrivacyRightsConfig,
  PrivacyRightsModelData,
  ConsentsStatus,
  FilteredPrivacyRightsModelData,
  InstructionItem,
  CheckboxItem,
  AdditionalSectionItem,
  ConsentVariant,
  PrivacyConsentFieldName,
  PrivacyRightsCheckboxFormValues,
  AuthMode,
} from '../PrivacyRightsWrapper.types';

import {
  DONOT_SELL_SHARE_SECTION_CODE,
  AKAMAI_COUNTRY_CODE_COOKIE,
  ONE_TRUST_COOKIE,
  VARIANT_RULES,
  CONSENT_PAYLOAD_MAPPING,
} from '../constant';

let fallbackIdCounter = 0;

export const getCookies = (name: string): string | undefined => {
  return Cookies.get(name);
};

const getUniqueId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  }

  fallbackIdCounter += 1;
  return `privacy-rights-${Date.now()}-${fallbackIdCounter}`;
};

export const getUserGPCStatus = () => ((navigator as any).globalPrivacyControl ? true : false);

const getPrivacyChoiceSubHeading = (
  isUsStateAndBelongToCpraGroups: boolean,
  isAuthenticated: boolean | undefined,
  subHeaders: {
    usAuth: string;
    usUnAuth: string;
    nonUSResident: string;
  }
) => {
  if (!subHeaders) {
    return '';
  }

  if (isUsStateAndBelongToCpraGroups) {
    return isAuthenticated ? subHeaders.usAuth : subHeaders.usUnAuth;
  }
  return subHeaders.nonUSResident;
};

export const getFilteredModelDataForPrivacyRights = (
  privacyRightsModelData: PrivacyRightsModelData,
  isAuthenticated: boolean | undefined,
  selectedStateOrLoggedInUserStateCode: string,
  currentConsentsStatus: ConsentsStatus,
  isUsStateAndBelongToCpraGroups: boolean
): FilteredPrivacyRightsModelData => {
  const isGPCEnabled = getUserGPCStatus();
  const filteredPrivacyRightsConfig = getFilteredPrivacyRightsConfig(
    privacyRightsModelData,
    isAuthenticated,
    selectedStateOrLoggedInUserStateCode,
    currentConsentsStatus,
    isGPCEnabled
  );

  let filteredModelJSON: FilteredPrivacyRightsModelData = {
    ...privacyRightsModelData,
    ...filteredPrivacyRightsConfig,
    statementHeaderForGPC: isGPCEnabled ? privacyRightsModelData.statementHeaderForGPC : '',
    statementTitle: isAuthenticated ? privacyRightsModelData.statementTitleAuth : privacyRightsModelData.statementTitle,
    // addtional parameters other than default AEM Model parameters
    subHeading:
      getPrivacyChoiceSubHeading(isUsStateAndBelongToCpraGroups, isAuthenticated, privacyRightsModelData.subHeaders) ||
      '',
    uniqueId: getUniqueId(),
  };

  // If user has not selected any of the valid state hide lower section other than header
  if (!isUsStateAndBelongToCpraGroups || selectedStateOrLoggedInUserStateCode === 'NONE') {
    const updatedJson = {
      statementHeaderForGPC: '',
      checkboxes: [],
      instructions: [],
      additionalSection: [],
    };

    filteredModelJSON = { ...filteredModelJSON, ...updatedJson };
  }

  // If user is Authenticated hide email input from UI
  if (isAuthenticated) {
    filteredModelJSON = { ...filteredModelJSON, form: null };
  }

  return filteredModelJSON;
};

export const filterListByAuthModeAndState = <T extends CheckboxItem | InstructionItem | AdditionalSectionItem>(
  list: T[],
  authMode: AuthMode,
  selectedStateCode: string
): (T & { uniqueId: string })[] => {
  const filteredList: (T & { uniqueId: string })[] = [];

  for (const item of list) {
    const matchesAuthMode = Array.isArray(item.authMode)
      ? item.authMode.includes(authMode)
      : item.authMode === authMode;

    const stateCodes = 'states' in item ? item.states : item.stateCode;

    const matchesState = stateCodes.includes(selectedStateCode);

    if (!matchesAuthMode || !matchesState) {
      continue;
    }

    filteredList.push({
      ...item,
      uniqueId: getUniqueId(),
    });
  }

  return filteredList;
};

const getConsentKey = ({ CCPA, CPRA }: ConsentsStatus) => `CCPA_${CCPA ? 'OUT' : 'IN'}_CPRA_${CPRA ? 'OUT' : 'IN'}`;

export const getConsentCheckboxes = (checkboxes: CheckboxItem[], consents: ConsentsStatus): CheckboxItem[] => {
  const result: CheckboxItem[] = [];

  // Generating opposite key as per the current consents
  // ex: opt_in -> opt_out
  const consentKey = getConsentKey(consents);
  const rule = VARIANT_RULES[consentKey];

  if (!rule) {
    return result;
  }

  const findCheckbox = (variant: string) => checkboxes.find(checkbox => checkbox.variant === variant);

  const addCheckbox = (variant: string) => {
    const checkbox = findCheckbox(variant);

    if (checkbox && !result.some(existing => existing.variant === checkbox.variant)) {
      result.push({ ...checkbox, uniqueId: getUniqueId() });
    }
  };

  /**
   * Include MASTER checkbox for ALL IN and ALL OUT scenarios.
   */
  if (rule.masterVariant) {
    addCheckbox(rule.masterVariant);
  }

  const primaryCheckbox = findCheckbox(rule.primary);

  /**
   * 1. Find primary combination variant
   */
  if (primaryCheckbox) {
    addCheckbox(rule.primary);

    /**
     * 2. Find opposite combination variant
     */
    const secondaryCheckbox = findCheckbox(rule.secondary);

    if (secondaryCheckbox) {
      addCheckbox(rule.secondary);
    } else {
      /**
       * Add secondary fallback variant
       */
      addCheckbox(rule.fallback);
    }

    return result;
  }

  /**
   * 3. Primary not found
   * Find secondary combination variant
   */
  const secondaryCheckbox = findCheckbox(rule.secondary);

  if (secondaryCheckbox) {
    addCheckbox(rule.secondary);

    /**
     * Add individual variant for the
     * second part of the secondary combination
     */
    addCheckbox(rule.individualVariants[0]);

    return result;
  }

  /**
   * Neither primary nor secondary exists
   * Add individual variants
   */
  addCheckbox(rule.individualVariants[0]);
  addCheckbox(rule.individualVariants[1]);

  return result;
};

export const getFilteredPrivacyRightsConfig: GetFilteredPrivacyRightsConfig = (
  privacyRightsModelData,
  isAuthenticated,
  selectedStateOrLoggedInUserStateCode,
  currentConsentsStatus,
  isGPCEnabled
) => {
  const authMode = isAuthenticated ? 'AUTH' : 'UNAUTH';
  const { checkboxes = [], instructions = [], additionalSection = [] } = privacyRightsModelData;
  const newConsents = isGPCEnabled ? { CCPA: false, CPRA: false } : currentConsentsStatus;
  const eligibleCheckboxes = filterListByAuthModeAndState(checkboxes, authMode, selectedStateOrLoggedInUserStateCode);
  const filteredCheckboxes: CheckboxItem[] = getConsentCheckboxes(eligibleCheckboxes, newConsents);
  const filteredInstructions: InstructionItem[] = filterListByAuthModeAndState(
    instructions,
    authMode,
    selectedStateOrLoggedInUserStateCode
  );
  const filteredAdditionalSection: AdditionalSectionItem[] = filterListByAuthModeAndState(
    additionalSection,
    authMode,
    selectedStateOrLoggedInUserStateCode
  );
  return {
    checkboxes: filteredCheckboxes,
    instructions: filteredInstructions,
    additionalSection: filteredAdditionalSection,
  };
};

export const defaultConsentsFromAkamaiCountryCodeCookie = (
  ccpaOptInCountries: string[],
  cpraOptInCountries: string[]
) => {
  const countryCodeFromCookies = getCookies(AKAMAI_COUNTRY_CODE_COOKIE)?.trim().toUpperCase();
  const defaultConsents = { CCPA: false, CPRA: false };

  if (!countryCodeFromCookies) {
    return defaultConsents;
  }

  const is_ccpa_opt_in = ccpaOptInCountries.includes(countryCodeFromCookies);
  const is_cpra_opt_in = cpraOptInCountries.includes(countryCodeFromCookies);

  if (is_ccpa_opt_in || is_cpra_opt_in) {
    return { CCPA: is_ccpa_opt_in, CPRA: is_cpra_opt_in };
  }

  return defaultConsents;
};

export const getConsentsFromCookies = (ccpaOptInCountries: string[], cpraOptInCountries: string[]) => {
  // Pre determine fallback default consents from AKAMAI_COUNTRY_CODE_COOKIE cookie
  const fallbackConsents = defaultConsentsFromAkamaiCountryCodeCookie(ccpaOptInCountries, cpraOptInCountries);

  // Step 1: Need to check for oneTrust Cookies on initial load
  const oneTrustCookie = getCookies(ONE_TRUST_COOKIE);

  // Step 2: If OneTrust cookies is not present get default consents
  if (!oneTrustCookie) {
    return fallbackConsents;
  }
  const decodedValue = decodeURIComponent(oneTrustCookie);
  const groups = new URLSearchParams(decodedValue)?.get('groups');

  // Step 3: If no groups are present in oneTrust cookies get default consents
  if (!groups) {
    return fallbackConsents;
  }

  const advertisingConsent = groups?.includes('4:1');

  const currentConsents = {
    CCPA: advertisingConsent,
    CPRA: advertisingConsent,
  };

  // currentConsents from oneTrust cookie
  return currentConsents;
};

export const getResolvedConsents = (
  pastConsents: ConsentsStatus | null,
  currentSelectedConsents: { CCPA?: boolean; CPRA?: boolean }
): ConsentsStatus => {
  return {
    CCPA: currentSelectedConsents.CCPA ?? pastConsents?.CCPA ?? false,
    CPRA: currentSelectedConsents.CPRA ?? pastConsents?.CPRA ?? false,
  };
};

export const updateConsentsToOneTrustCookie = (consents: ConsentsStatus): void => {
  if (typeof window === 'undefined') {
    console.error('SSR environment detected');
    return;
  }

  const consentManager = window['ConsentManager'];
  if (!consentManager || typeof consentManager !== 'function') {
    console.error('[ConsentManager] window.ConsentManager is not available or returned an invalid instance.');
    return;
  }

  const _consentManger = consentManager();
  if (typeof _consentManger.optInMarketing !== 'function') {
    console.error('ConsentManager.optInMarketing is not available');
    return;
  }

  if (typeof _consentManger.optOutMarketing !== 'function') {
    console.error('ConsentManager.optOutMarketing is not available');
    return;
  }

  const advertisingEnabled = consents.CCPA && consents.CPRA;
  if (advertisingEnabled) {
    _consentManger.optInMarketing();
  } else {
    _consentManger.optOutMarketing();
  }
};

export const transformCustomerData = (response: PhoenixLegalPrivacyConsentResponseResult) => {
  const customer = response?.customer;
  if (!customer) {
    return null;
  }

  const { givenName, fullName } = customer.profile.name;
  const privacyConsentFeatures = customer.featuresByGroup.privacyConsentFeatures;
  const addresses = customer.contactInformation.addresses;

  const { DONOT_SELL_FETCH_SECTION_CODE, DONOT_SHARE_FETCH_SECTION_CODE, DONOT_SELL_SHARE_OPTIN_VALUE } =
    DONOT_SELL_SHARE_SECTION_CODE;

  const CCPA = privacyConsentFeatures.some(
    feature => feature.feature.code === DONOT_SELL_FETCH_SECTION_CODE && feature.value === DONOT_SELL_SHARE_OPTIN_VALUE
  );

  const CPRA = privacyConsentFeatures.some(
    feature => feature.feature.code === DONOT_SHARE_FETCH_SECTION_CODE && feature.value === DONOT_SELL_SHARE_OPTIN_VALUE
  );

  const primaryAddress = addresses.length > 0 ? addresses[0].address : null;
  const stateCode = primaryAddress?.stateProvince ?? null;
  const countryCode = primaryAddress?.country?.code ?? null;

  return {
    name: givenName || fullName,
    stateCode,
    countryCode,
    CCPA,
    CPRA,
  };
};

export const capitalizeFirstLetter = (inputWord: string) =>
  inputWord.charAt(0).toUpperCase() + inputWord.slice(1).toLowerCase();

export const getCheckboxFieldName = (variant: ConsentVariant): PrivacyConsentFieldName => `privacyConsents.${variant}`;

export const getMasterChildren = (
  masterVariant: ConsentVariant,
  renderedCheckboxes: CheckboxItem[]
): ConsentVariant[] => {
  if (masterVariant === 'MASTER_IN') {
    return renderedCheckboxes
      .filter(
        checkbox =>
          checkbox.variant !== 'MASTER_IN' &&
          (checkbox.variant.startsWith('CCPA_IN') || checkbox.variant.startsWith('CPRA_IN'))
      )
      .map(checkbox => checkbox.variant);
  }

  if (masterVariant === 'MASTER_OUT') {
    return renderedCheckboxes
      .filter(
        checkbox =>
          checkbox.variant !== 'MASTER_OUT' &&
          (checkbox.variant.startsWith('CCPA_OUT') || checkbox.variant.startsWith('CPRA_OUT'))
      )
      .map(checkbox => checkbox.variant);
  }

  return [];
};

export const getParentMaster = (variant: ConsentVariant): ConsentVariant | null => {
  if (variant === 'MASTER_IN' || variant === 'MASTER_OUT') {
    return null;
  }

  if (variant.includes('IN')) {
    return 'MASTER_IN';
  }

  if (variant.includes('OUT')) {
    return 'MASTER_OUT';
  }

  return null;
};

export const getNormalizedVariants = (variant: string): Array<keyof typeof CONSENT_PAYLOAD_MAPPING> => {
  // Ignore master variants.
  if (variant === 'MASTER_IN' || variant === 'MASTER_OUT') {
    return [];
  }

  // Mixed / combination variants.
  // Only use the first half.
  if (variant.includes(':')) {
    return [variant.split(':')[0] as keyof typeof CONSENT_PAYLOAD_MAPPING];
  }

  // Individual variants.
  return [variant as keyof typeof CONSENT_PAYLOAD_MAPPING];
};

export const getConsentsForPayload = (data: PrivacyRightsCheckboxFormValues) => {
  const { privacyConsents } = data || {};

  if (!privacyConsents) {
    return [];
  }

  const payload = [];

  for (const [variant, isSelected] of Object.entries(privacyConsents)) {
    if (!isSelected) {
      continue;
    }

    const normalizedVariants = getNormalizedVariants(variant);

    for (const normalizedVariant of normalizedVariants) {
      const consent = CONSENT_PAYLOAD_MAPPING[normalizedVariant];

      if (consent) {
        payload.push(consent);
      }
    }
  }

  return payload;
};
